// externals
import type { FC, FocusEvent } from 'react';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

// libraries
import type { SelectInputProps } from '@interstate/components/SelectInput';
import type { ComboBoxOptions, ComboBoxProps, ComboBoxOption } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { selectFocusedInput, useSelectOnFocusEnabledWithLegacyFallback } from '../selectOnFocus/selectOnFocus';
import type { SelectOnFocusSelector } from '../selectOnFocus/selectOnFocus';
import type { DropdownOptionWithDescription } from './DropdownWithDescription';

// utils
import { insertInjectedIcon } from './dropdownDomInjection';

// styles
import { ComboBoxContainer } from './Dropdown.style';

type DropdownProps = Omit<SelectInputProps, 'value' | 'options'> &
    Omit<ComboBoxProps, 'value' | 'onChange' | 'onBlur' | 'options'> & {
        value: string | undefined;
        allowRemoval?: boolean;
        minWidthForOptions?: string;
        options: DropdownOptionWithDescription[];
        selectOnFocusSelector?: SelectOnFocusSelector;
    };

export const Dropdown: FC<DropdownProps> = ({
    value,
    minWidthForOptions,
    allowRemoval = false,
    sx,
    selectOnFocusSelector,
    ...commonProps
}) => {
    const previousSelectedValueRef = useRef<string | undefined>(value);
    const [displayValue, setDisplayValue] = useState(value);
    const containerRef = useRef<HTMLDivElement>(null);
    const shouldRestoreFocusRef = useRef(false);
    const isRestoringFocusRef = useRef(false);
    const componentInstanceId = useId();

    const isAutoSelectEnabled = useSelectOnFocusEnabledWithLegacyFallback(selectOnFocusSelector);

    // onFocus is placed on the container (React focus events delegate from
    // descendants) because the top-level ComboBox props don't expose onFocus.
    const handleComboBoxFocus = (e: FocusEvent<HTMLElement>) => {
        // Skip the programmatic focus-restore after picking an option — that's not a
        // click-to-edit, so it shouldn't auto-select the value.
        /* istanbul ignore next -- restore re-focuses the real ComboBox input in-browser;
           not reproducible in jsdom, where the combo is mocked without that input */
        if (isRestoringFocusRef.current) return;
        if (isAutoSelectEnabled) selectFocusedInput(e);
    };

    useEffect(() => {
        if (value !== displayValue) {
            setDisplayValue(value);
        }

        previousSelectedValueRef.current = value;

        // Restore focus after value change when user selects option
        if (shouldRestoreFocusRef.current) {
            setTimeout(() => {
                if (containerRef.current) {
                    const input = containerRef.current.querySelector('input');
                    if (input) {
                        // Bracket the programmatic focus so handleComboBoxFocus can
                        // tell it apart from a user click and skip select-all.
                        isRestoringFocusRef.current = true;
                        input.focus();
                        isRestoringFocusRef.current = false;
                    }
                }
            }, 50);
            shouldRestoreFocusRef.current = false;
        }
    }, [value]);

    const getComboBoxValue = useMemo((): ComboBoxOptions | undefined => {
        if (!displayValue) return undefined;
        const selectedOption = commonProps.options.find(
            (obj) => obj.value === displayValue && !obj.isGroupHeader && !obj.groupHeader
        );
        return selectedOption?.label ? [selectedOption as ComboBoxOption] : undefined;
    }, [displayValue, commonProps.options]);

    const getComboboxValueFromEvent = (e: InterstateOnChangeEvent<ComboBoxOptions>): string => {
        return e.target.value && e.target.value.length > 0 ? e.target.value[0].value : '';
    };

    const handleComboBoxChange = (e: InterstateOnChangeEvent<string | ComboBoxOptions>) => {
        if (!commonProps.onChange || e.domEvent) return;

        const selectedValue = getComboboxValueFromEvent(e as InterstateOnChangeEvent<ComboBoxOptions>);

        const selectedOption = commonProps.options.find((opt) => opt.value === selectedValue);
        if (selectedOption?.isGroupHeader || selectedOption?.groupHeader) return;

        setDisplayValue(selectedValue);
        e.target.value = selectedValue;

        if (allowRemoval || selectedValue) {
            shouldRestoreFocusRef.current = true;
            commonProps.onChange(e as InterstateOnChangeEvent<string>);
        }
    };

    const handleComboBoxBlur = (e: InterstateOnChangeEvent<string | ComboBoxOptions>) => {
        let selectedValue = getComboboxValueFromEvent(e as InterstateOnChangeEvent<ComboBoxOptions>);

        // When allowRemoval is false; If a value is removed, we reset back to the previous value if it exists to prevent user from removing selection.
        if (!allowRemoval && !selectedValue && previousSelectedValueRef.current) {
            setDisplayValue(previousSelectedValueRef.current);
            selectedValue = previousSelectedValueRef.current;
        }
        e.target.value = selectedValue;
        commonProps.onBlur?.(e as InterstateOnChangeEvent<string>);
    };

    // Use MutationObserver to detect when Popper is added to DOM and set min-width directly
    useEffect(() => {
        if (!minWidthForOptions) return;

        const observer = new MutationObserver(() => {
            const popper = document.querySelector('.base-Popper-root') as HTMLElement;
            if (popper && popper.style.minWidth !== minWidthForOptions) {
                popper.style.minWidth = minWidthForOptions;
            }
        });

        // Observe document body for child additions (Popper renders here)
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        return () => {
            observer.disconnect();
        };
    }, [minWidthForOptions]);

    const findAssociatedPopoverForThisComponent = useCallback((): HTMLElement | null => {
        /* istanbul ignore next */
        if (!containerRef.current) return null;

        const comboBoxInput = containerRef.current.querySelector<HTMLElement>(
            'input[id*="ids-combo-box-input"], input[data-testid*="combo-box"]'
        );
        /* istanbul ignore next */
        if (!comboBoxInput) return null;

        const poppers = document.querySelectorAll<HTMLElement>('.base-Popper-root, .MuiPopper-root');
        for (const popper of Array.from(poppers)) {
            const listContainer = popper.querySelector<HTMLElement>('ul[role="menu"]');
            const firstItem = listContainer?.querySelector('li[role="menuitem"]');
            const firstItemText = firstItem?.textContent?.trim() || '';
            if (firstItem && commonProps.options.some((opt) => opt.label.trim() === firstItemText)) {
                return listContainer;
            }
        }

        return null;
    }, [commonProps.options]);

    const injectIconsForThisComponent = useCallback(() => {
        const listContainer = findAssociatedPopoverForThisComponent();
        if (!listContainer) return;

        if (listContainer.dataset.componentInstanceId && listContainer.dataset.componentInstanceId !== componentInstanceId) return;
        listContainer.dataset.componentInstanceId = componentInstanceId;

        listContainer.querySelectorAll('li[role="menuitem"]').forEach((item) => {
            const idsItem = item.querySelector<HTMLElement>('.ids-list-box-item');
            if (!idsItem) return;

            /* istanbul ignore next */
            const labelText = idsItem.textContent?.trim() || '';
            const matchingOption = commonProps.options.find((opt) => opt.label.trim() === labelText);
            if (!matchingOption?.hasIcon || matchingOption.isGroupHeader || matchingOption.groupHeader) return;

            insertInjectedIcon(idsItem, componentInstanceId);
        });
    }, [commonProps.options, findAssociatedPopoverForThisComponent, componentInstanceId]);

    // Only wired up when at least one option asks for an icon so unflagged dropdowns pay nothing.
    const hasIconOptions = useMemo(() => commonProps.options.some((opt) => opt.hasIcon), [commonProps.options]);

    useEffect(() => {
        if (!hasIconOptions) return;

        const observer = new MutationObserver(() => {
            injectIconsForThisComponent();
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Fallback for list content that changes without a body mutation (e.g. filtering)
        const intervalId = setInterval(() => {
            const popover = findAssociatedPopoverForThisComponent();
            if (popover) {
                injectIconsForThisComponent();
            }
        }, 200);

        return () => {
            observer.disconnect();
            clearInterval(intervalId);
        };
    }, [hasIconOptions, injectIconsForThisComponent, findAssociatedPopoverForThisComponent]);

    const optionsWithDisabledHeaders = commonProps.options.map((opt) => ({
        ...opt,
        disabled: opt.isGroupHeader || opt.groupHeader
    }));

    return (
        <ComboBoxContainer
            ref={containerRef}
            onFocus={handleComboBoxFocus}
            data-testid={`${commonProps['data-testid']}-container`}
            data-component-instance-id={componentInstanceId}
        >
            <ComboBox
                {...commonProps}
                options={optionsWithDisabledHeaders}
                value={getComboBoxValue}
                onChange={handleComboBoxChange}
                allowNewEntry={false}
                onBlur={handleComboBoxBlur}
                sx={{
                    '& .ids-combo-box-multi-select-input': {
                        padding: 0
                    },
                    ...sx
                }}
                required={commonProps.required && !value}
            />
        </ComboBoxContainer>
    );
};
